import { API_BASE_URL } from 'config/env-config';
import { createApiCall } from 'lib/base';
import { staffSessionHeaders } from 'utils/auth';
import { TwoFactorSetup } from './two-factor';

// After Google sign-in, staff enter a second code: from their authenticator
// app if they set one up (account security page), otherwise one emailed to
// them. Roles that require an authenticator (admin, superadmin) set one up
// here first. The session token it returns goes in X-Staff-Session on every API call.

export type SignInMethod = 'authenticator' | 'email' | 'setup';

export type { TwoFactorSetup };

export interface SignInMethodResponse {
  method: SignInMethod;
  email: string;
}

export interface EmailOtpSendResponse {
  email: string;
  expires_at: string;
  resend_after: string;
}

export interface StaffSessionResponse {
  session_token: string;
  expires_at: string;
  // only right after setup at sign-in; shown once
  backup_codes?: string[];
}

// Sign-in steps: the page handles every error (no redirect on 401).
export const signInMethodApi = createApiCall<void>('SIGN_IN_METHOD', () => ({
  endpoint: `api/admin/auth/sign-in-method`,
  method: 'GET',
  skipDefaultErrorHandling: true,
}));

export const verifyAuthenticatorApi = createApiCall<{ code: string }>(
  'SIGN_IN_VERIFY_AUTHENTICATOR',
  body => ({
    endpoint: `api/admin/auth/two-factor/verify`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

export const setupAuthenticatorApi = createApiCall<void>(
  'SIGN_IN_SETUP_AUTHENTICATOR',
  () => ({
    endpoint: `api/admin/auth/two-factor/setup`,
    method: 'POST',
    skipDefaultErrorHandling: true,
  })
);

export const enableAuthenticatorApi = createApiCall<{ code: string }>(
  'SIGN_IN_ENABLE_AUTHENTICATOR',
  body => ({
    endpoint: `api/admin/auth/two-factor/enable`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

export const sendEmailOtpApi = createApiCall<void>('SEND_EMAIL_OTP', () => ({
  endpoint: `api/admin/auth/email-otp/send`,
  method: 'POST',
  skipDefaultErrorHandling: true,
}));

export const verifyEmailOtpApi = createApiCall<{ code: string }>(
  'VERIFY_EMAIL_OTP',
  body => ({
    endpoint: `api/admin/auth/email-otp/verify`,
    method: 'POST',
    body,
    skipDefaultErrorHandling: true,
  })
);

// Ends the staff session on the server; best effort, never throws. The
// session token alone is enough, so this works after the Google ID token has
// expired.
export async function signOutStaffSession() {
  const session = staffSessionHeaders();
  if (!Object.keys(session).length) return;
  try {
    await fetch(`${API_BASE_URL}/api/admin/auth/sign-out`, {
      method: 'POST',
      headers: session,
    });
  } catch {
    // signing out locally is enough
  }
}

const errorMessages: Record<string, string> = {
  invalid_email_otp: 'Mã xác thực không đúng.',
  email_otp_expired: 'Mã xác thực đã hết hạn, vui lòng gửi lại mã mới.',
  email_otp_too_many_attempts:
    'Bạn đã nhập sai quá nhiều lần, vui lòng gửi lại mã mới.',
  email_otp_resend_too_soon: 'Vui lòng chờ một chút trước khi gửi lại mã.',
  email_otp_too_many_sends:
    'Bạn đã yêu cầu quá nhiều mã, vui lòng thử lại sau.',
  email_otp_unavailable:
    'Không thể gửi mã xác thực lúc này, vui lòng thử lại sau.',
  email_otp_not_allowed:
    'Tài khoản đã bật ứng dụng xác thực, vui lòng dùng mã từ ứng dụng.',
  two_factor_setup_required:
    'Vai trò của bạn bắt buộc dùng ứng dụng xác thực, vui lòng đăng nhập lại để thiết lập.',
  staff_session_unavailable:
    'Không thể đăng nhập lúc này, vui lòng thử lại sau.',
};

// A Vietnamese message for a failed sign-in step; falls back to the 2FA
// messages (authenticator codes) via the given fallback.
export const signInErrorMessage = (
  err: any,
  fallback: (err: any) => string
): string => {
  if (err?.status === 403) {
    return 'Tài khoản Google này không có quyền truy cập Stag Admin.';
  }
  const code = err?.response?.error_code;
  if (code && errorMessages[code]) return errorMessages[code];
  return fallback(err);
};
